import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useBoolean } from "react-hanger";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { useDropzone } from "react-dropzone";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import {
  DataGrid,
  GridColDef,
} from "@mui/x-data-grid";
import moment from "moment";

import { siteConstants } from "../../../constants";
import { alertActions, thinkificActions } from "../../../redux/actions";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const getDropzoneBackgroundColor = (props) => {
  if (props.isDragActive)
    return "#fafafa";

  return "#ebebeb";
}

const DropzoneContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  padding: 20px;
  border-width: 1px;
  border-radius: 4px;
  border-style: dashed;
  border-color: #aaaaaa;
  background-color: ${props => getDropzoneBackgroundColor(props)};
  color: #8a8a8a;
  outline: none;
`;

const StyledDropzone = (props) => {
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    maxFiles: 1,
    multiple: false,
  });

  useEffect(() => {
    if (acceptedFiles.length > 0)
      props.onFileAccepted(acceptedFiles[0]);
  }, [acceptedFiles]);

  useEffect(() => {
    if (fileRejections.length > 0)
      props.onFileRejected();
  }, [fileRejections]);

  return (
    <>
      <DropzoneContainer {...getRootProps({ isDragActive, isDragAccept, isDragReject })}>
        <input {...getInputProps()} />
        <Typography align="center" variant="subtitle2">
          <p>Drag 'n' drop Thinkific discount codes file here, or click to select file</p>
        </Typography>
        <CloudUploadIcon />
      </DropzoneContainer>
      {acceptedFiles.length > 0 &&
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="caption">
            {acceptedFiles[0].name} ({(acceptedFiles[0].size / 1000).toFixed(1)} KB)
          </Typography>
        </Box>
      }
      {fileRejections.length > 0 &&
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="caption">
            {fileRejections[0].file.name} ({(fileRejections[0].file.size / 1000).toFixed(1)} KB)
          </Typography>
        </Box>
      }
    </>
  )
}

const ThinkificDiscountCodesPage = (props) => {
  const [discountCodes, setDiscountCodes] = useState([]);
  const [discountCodesFile, setDiscountCodesFile] = useState(null);
  const isConfirmationDialogOpen = useBoolean(false);

  const columns: GridColDef[] = [
    { field: "code", headerName: "Code", width: 120, },
    { field: "claimed", headerName: "Claimed", width: 120, },
    { field: "claimedTimestamp", headerName: "Claimed Timestamp", width: 160, },
    { field: "clientName", headerName: "Client Name", flex: 1, },
  ];

  const onFileAccepted = (file) => {
    props.alertReset();
    setDiscountCodesFile(file);
  }

  const onFileRejected = () => {
    props.alertError(siteConstants.ERROR_MSG_INVALID_THINKIFIC_DISCOUNT_CODES_FILE);
  }

  const handleConfirmationDialogClose = () => {
    isConfirmationDialogOpen.setFalse();
  }

  const handleContinueButtonClick = () => {
    isConfirmationDialogOpen.setFalse();
    props.createDiscountCodes(discountCodesFile);
  }

  useEffect(() => {
    if (discountCodesFile)
      isConfirmationDialogOpen.setTrue();
  }, [discountCodesFile]);

  useEffect(() => {
    const _discountCodes = [];
    if (props.discountCodes) {
      props.discountCodes.map((row) => {
        const timestamp = moment(row.order?.modified);
        _discountCodes.push({
          id: row.id,
          code: row.code,
          claimed: row.claimed,
          claimedTimestamp: row.claimed ? timestamp.fromNow() : null,
          clientName: row.order?.cs_reports[0].client_name,
        })
      });
    }
    setDiscountCodes(_discountCodes);
  }, [props.discountCodes]);

  useEffect(() => {
    props.alertReset();
    props.listDiscountCodes();
  }, []);

  return (
    <>
      <Helmet>
        <title>Thinkific Discount Codes</title>
      </Helmet>

      <Container
        maxWidth="xl"
        sx={{
          marginTop: "30px",
          textAlign: "left",
        }}
      >
        <Typography variant="h4">Thinkific Discount Codes</Typography>
        {
          props.alert.message &&
          <Alert variant="outlined" severity={ props.alert.type }>
            { props.alert.message }
          </Alert>
        }
        {discountCodes &&
          <Card variant="outlined" sx={{ marginTop: "20px" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <Typography sx={{flex: 1}}>Claimed: { discountCodes.filter(x => x.claimed).length }</Typography>
                <Typography>
                  Available: { discountCodes.filter(x => !x.claimed).length }
                </Typography>
              </Box>
            </CardContent>
          </Card>
        }
        <StyledDropzone
          selectedFilePath={props.selectedFilePath}
          selectedFileSize={props.selectedFileSize}
          onFileAccepted={onFileAccepted}
          onFileRejected={onFileRejected}
        />
        <DataGrid
          rows={discountCodes}
          columns={columns}
          autoHeight={true}
          density="compact"
          hideFooterSelectedRowCount={true}
          loading={props.isListingDiscountCodes || props.isCreatingDiscountCodes}
          sx={{ marginTop: "20px" }}
        />
      </Container>
      {discountCodesFile &&
        <Dialog
          open={isConfirmationDialogOpen.value}
          onClose={handleConfirmationDialogClose}
          aria-labelledby="confirmation-dialog-title"
          aria-describedby="confirmation-dialog-description"
        >
          <DialogTitle id="confirmation-dialog-title">
            Create Thinkific Discount Codes
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="confirmation-dialog-description">
              You are about to create Thinkific discount codes with "{discountCodesFile.name}".
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={handleContinueButtonClick}
              sx={{ mt: 1, mr: 1 }}
            >
              Continue
            </Button>
            <Button
              variant="outlined"
              onClick={handleConfirmationDialogClose}
              sx={{ mt: 1, mr: 1 }}
            >
              Back
            </Button>
          </DialogActions>
        </Dialog>
      }
    </>
  );
}

const mapState = (state) => {
  const {
    isListingDiscountCodes,
    discountCodes,
    isCreatingDiscountCodes,
  } = state.thinkific;
  return {
    isListingDiscountCodes,
    discountCodes,
    isCreatingDiscountCodes,
    alert: state.alert,
  }
};

const actionCreators = {
  alertReset: alertActions.reset,
  alertError: alertActions.error,
  listDiscountCodes: thinkificActions.listDiscountCodes,
  createDiscountCodes: thinkificActions.createDiscountCodes,
};

export default connect(mapState, actionCreators)(ThinkificDiscountCodesPage);